import * as React from "react"

/**
 * Blocks portalled overlay surfaces while the app is in a non-interactive
 * state, e.g. confirmed server-offline.
 *
 * Radix portals render to document.body, so DOM-level `inert` on an app
 * wrapper never reaches them — but React context does. Mount this provider
 * around the app tree and Dialog/Sheet/Select/Popover/DropdownMenu (plus
 * MediaPreview and other DialogPrimitive consumers) lock themselves: `inert`,
 * dimmed, and with escape/outside-click dismissal suppressed so open
 * overlay state survives until the app unlocks.
 */
const OverlayLockContext = React.createContext(false)

export interface OverlayLockProviderProps {
  locked: boolean
  children: React.ReactNode
}

export function OverlayLockProvider({ locked, children }: OverlayLockProviderProps) {
  return <OverlayLockContext.Provider value={locked}>{children}</OverlayLockContext.Provider>
}

/** True while overlay surfaces must be non-interactive. Defaults to false. */
export function useOverlayLock(): boolean {
  return React.useContext(OverlayLockContext)
}

/**
 * Visual treatment for locked overlay content — mirrors the authenticated
 * shell's offline filter so the whole screen reads as one blocked surface.
 */
export const OVERLAY_LOCKED_CLASSES =
  "pointer-events-none select-none opacity-50 saturate-[0.2] brightness-[0.96]"

/**
 * Wraps an overlay dismissal handler (Escape, outside interaction) so it is
 * suppressed while locked. Keeps Dialog and Sheet lock semantics identical.
 */
export function guardWhenLocked<E extends { preventDefault(): void }>(
  isLocked: boolean,
  handler?: (event: E) => void,
): (event: E) => void {
  return (event) => {
    if (isLocked) {
      event.preventDefault()
      return
    }
    handler?.(event)
  }
}
